import { useState, useEffect, useRef } from "react"
import { Text, Input, Textarea, Loading } from "@nextui-org/react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams, useNavigate } from "react-router-dom"
import validator from 'validator'

import Button from "../../../ui/button"
import PagePath from "../../../ui/pagePath"
import { Content } from "../../../ui/layout/content"
import { Box } from "../../../ui/layout/box"
import Header from "../../../ui/header"
import {
    getApplication,
    createApplication,
    API_PENDING,
    API_SUCCESS_CREATE_APPLICATION,
    API_SUCCESS_GET_APPLICATION
} from '../../../data/appData'

const Question = ({name, label, inputRef, formData, setFormData, error, isLongAnswer=false}) => {
    return (
        <Content spacerSize={1} contentCss={{
            '@xsMax': {
                width: "100%"
            },
            '@xsMin': {
                width: "50%"
            }
        }}>
            <Text 
                size="$sm" 
                color={error ? 'error' : 'default'}
                css={{ta: "left", mb: "$sm"}}>{name}</Text>
            {
                isLongAnswer &&
                <Textarea
                    ref={inputRef}
                    animated={false}
                    aria-label={label}
                    css={{width: "100%"}}
                    bordered 
                    color="primary" 
                    onChange={(e) => {
                        inputRef.current.value = e.target.value
                        setFormData({
                            ...formData,
                            [label]: e.target.value
                        })
                    }}
                    />
            }
            {
                !isLongAnswer &&
                <Input 
                    ref={inputRef}
                    animated={false}
                    aria-label={label}
                    css={{width: "100%"}}
                    bordered 
                    color="primary" 
                    onChange={(e) => {
                        inputRef.current.value = e.target.value
                        setFormData({
                            ...formData,
                            [label]: e.target.value
                        })
                    }}
                    />
            }
        </Content>
    )
}

export default({}) => {
    const dispatch = useDispatch()  
    const navigate = useNavigate()  
    const [searchParams] = useSearchParams()
    const apiCallingStatus = useSelector(state => state.appData.appAPICallingStatus)
    const applications = useSelector(state => state.appData.applications)
    // Flag to indicate if needs to check if this opportunity has already been applied
    const [alreadyApplied, setAlreadyApplied] = useState(false)

    // Data used for validation
    const els = {
        firstName: useRef(null),
        lastName: useRef(null),
        email: useRef(null),
        companyName: useRef(null),
        companyWebsite: useRef(null),
        problems: useRef(null),
        questions: useRef(null)
    }
    // Data used for submission
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
        companyWebsite: "",
        problems: "",
        questions: ""
    })

    const [error, setError] = useState({
        firstName: false,
        lastName: false,
        email: false,
        companyName: false,
        companyWebsite: false,
        problems: false,
        questions: false
    })
    const [valid, setValid] = useState(true)

    useEffect(() => {
        window.scrollTo(0,0)

        if (!searchParams.get('id')) {
            navigate('/opportunities')
        }
    }, [])

    useEffect(() => {
        if (apiCallingStatus === API_PENDING) {
            console.log('API calling')
        } else if (apiCallingStatus === API_SUCCESS_GET_APPLICATION) {
            console.log('API GET APPLICATION calling successful')
            console.log(applications)
            if (applications.length > 0) {
                // This application has already been applied
                setAlreadyApplied(true)
            } else {
                // Create the application
                dispatch(createApplication({
                    id: searchParams.get('id'), 
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    companyName: formData.companyName,
                    companyWebsite: formData.companyWebsite,
                    problems: formData.problems,
                    questions: formData.questions
                }))
                console.log('Create application')
            }
        } else if (apiCallingStatus === API_SUCCESS_CREATE_APPLICATION) {
            console.log('Application created')
            navigate('/opportunities/apply/confirmation', {replace: true})
        }
    }, [apiCallingStatus, applications])

    const validate = () => {
        let valid = true
        const _error = {
            firstName: false,
            lastName: false,
            email: false,
            companyName: false,
            companyWebsite: false,
            problems: false
        }
        const _labels = Object.keys(els)
        for (let i = 0; i < _labels.length; i++) {
            if (_labels[i] !== 'questions') {
                if (els[_labels[i]].current && !els[_labels[i]].current.value) {
                    valid = false
                    _error[_labels[i]] = true
                } else if (_labels[i] === 'email' && els[_labels[i]].current &&
                            !validator.isEmail(els[_labels[i]].current.value)) {
                    valid = false
                    _error[_labels[i]] = true
                } else if (_labels[i] === 'companyWebsite' && els[_labels[i]].current &&
                            !validator.isURL(els[_labels[i]].current.value)) {
                    valid = false
                    _error[_labels[i]] = true
                }
            }
        }

        setError(_error)
        setValid(valid)

        if (!valid) {
            window.scrollTo(0,0)
        } else {
            // Check if this opportunity has already been applied by this user
            dispatch(getApplication(searchParams.get('id')))
        }
    }

    return (
        <>
            <PagePath paths={[
                {
                    value: "Home",
                    href: "/"
                },
                {
                    value: "Opportunities",
                    href: "/opportunities"
                },
                {
                    value: "Apply"
                }
            ]} />
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Content spacerSize={2}>
                    <Box>
                        <Header h2 centered value="Get Started"/> 
                    </Box>
                </Content>
                <Content spacerSize={1}>
                    <Header h3 centered value="Tell Us a Little Bit about You and Your Business" />
                    <Text className="font-text" size="$sm" css={{lineHeight: "$sm", mt: "$sm", ta: "center"}}>This will help us identify the best research partner.</Text>
                </Content>
                {
                    apiCallingStatus === API_PENDING &&
                    <Content spacerSize={1}>
                        <Loading />
                    </Content>
                }
                {
                    alreadyApplied &&
                    <Content spacerSize={1}>
                        <Box css={{backgroundColor: "$error", padding: "10px"}}>
                            <Text className="font-text" size="$sm" color="#ffffff">You have already applied for this opportunity. We'll be in touch soon. Please stay tuned.</Text>
                        </Box>
                    </Content>
                }
                {
                    apiCallingStatus !== API_PENDING &&
                    !alreadyApplied &&
                    <>
                        {
                            !valid &&
                            <Content spacerSize={1}>
                                <Box css={{backgroundColor: "$error", padding: "10px"}}>
                                    <Text className="font-text" size="$sm" color="#ffffff">Please answer below questions before proceeding.</Text>
                                    <ul style={{padding: 0, margin: 0}}>
                                        {error.firstName && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Your First Name</Text></li>}
                                        {error.lastName && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Your Last Name</Text></li>}
                                        {error.email && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Your Email</Text></li>}
                                        {error.companyName && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Company Name</Text></li>}
                                        {error.companyWebsite && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Company Website</Text></li>}
                                        {error.problems && <li style={{margin: 0}}><Text className="font-text" size="$xs" color="#ffffff" b>- Problems You Aim to Solve</Text></li>}
                                    </ul>
                                </Box>
                            </Content>
                        }
                        <Question
                            name="Your First Name" 
                            label="firstName"
                            inputRef={els.firstName}
                            formData={formData}
                            setFormData={setFormData}
                            error={error.firstName} />
                        <Question 
                            name="Your Last Name"  
                            inputRef={els.lastName}
                            formData={formData}
                            setFormData={setFormData}
                            label="lastName" 
                            error={error.lastName}/>
                        <Question 
                            name="Your Email"  
                            inputRef={els.email}
                            formData={formData}
                            setFormData={setFormData}
                            label="email" 
                            error={error.email}/>
                        <Question 
                            name="Company Name"  
                            inputRef={els.companyName}
                            formData={formData} 
                            setFormData={setFormData}
                            label="companyName" 
                            error={error.companyName}/>
                        <Question 
                            name="Company Website"  
                            inputRef={els.companyWebsite} 
                            formData={formData}
                            setFormData={setFormData}
                            label="companyWebsite" 
                            error={error.companyWebsite}/>
                        <Question 
                            name="Tell Us (At a High Level) What Problem(s) You Are Aiming to Solve via This Opportunity" isLongAnswer 
                            inputRef={els.problems} 
                            formData={formData}
                            setFormData={setFormData}
                            label="problems" 
                            error={error.problems}/>
                        <Question 
                            name="Any Questions You Have for Us (Optional)" 
                            isLongAnswer  
                            inputRef={els.questions} 
                            formData={formData}
                            setFormData={setFormData}
                            label="questions" 
                            error={error.questions}/>
                        {
                            !alreadyApplied &&
                            <Content spacerSize={3}>
                                <Button 
                                    text="Confirm & Submit"
                                    onClick={() => {
                                        validate()
                                    }}/>
                            </Content>
                        }
                    </>
                }
                <Content spacerSize={4}></Content>
            </div>
        </>
    )
}